import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, DollarSign, Package, Truck, BarChart3, PieChart as PieIcon } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = ({ userId }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchAnalyticsData();
    }
  }, [userId]);

  const fetchAnalyticsData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/${userId}`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4'];

  // Main Metrics
  const totalSales = analyticsData?.totalSales || 0;
  const netProfit = analyticsData?.netProfit || 0;
  const activeAds = analyticsData?.activeAds || 0;

  // Order Analytics
  const inTransit = analyticsData?.inTransit || 0;
  const delivered = analyticsData?.delivered || 0;
  const returned = analyticsData?.returned || 0;

  // Gross Margin
  const deliveredSales = analyticsData?.deliveredSales || 0;
  const productCost = analyticsData?.productCost || 0;
  const deliveryCharges = analyticsData?.deliveryCharges || 0;
  const grossMargin = analyticsData?.grossMargin || 0;

  // Net Margin
  const adsSpend = analyticsData?.adsSpend || 0;
  const rtoCharges = analyticsData?.rtoCharges || 0;

  // Sales Breakdown Pie Chart
  const salesBreakdown = [
    { name: 'Product Sales', value: analyticsData?.productSales || 1 },
    { name: 'Shipping', value: analyticsData?.shippingRevenue || 1 },
    { name: 'Other', value: analyticsData?.otherRevenue || 1 }
  ];

  // Order Distribution Pie Chart
  const orderDistribution = [
    { name: 'In Transit', value: inTransit || 1 },
    { name: 'Delivered', value: delivered || 1 },
    { name: 'Returned', value: returned || 1 }
  ];

  // Cost Breakdown Pie Chart
  const costBreakdown = [
    { name: 'Product Cost', value: productCost || 1 },
    { name: 'Delivery', value: deliveryCharges || 1 },
    { name: 'Ads Spend', value: adsSpend || 1 },
    { name: 'RTO Charges', value: rtoCharges || 1 }
  ];

  // Revenue Comparison Bar Chart
  const revenueComparison = analyticsData?.monthlyRevenue || [
    { month: 'Jan', revenue: 0, expenses: 0 },
    { month: 'Feb', revenue: 0, expenses: 0 },
    { month: 'Mar', revenue: 0, expenses: 0 },
    { month: 'Apr', revenue: 0, expenses: 0 }
  ];

  return (
    <div className="space-y-6">
      {/* Main Metrics */}
      <div className="grid [&>*]:min-w-0 grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-blue-400" />
            </div>
            <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              ₹{totalSales}
            </div>
            <div className="text-xs text-slate-600 mt-1">Total Sales</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
            <div className="text-2xl md:text-3xl font-bold text-blue-600">
              ₹{netProfit}
            </div>
            <div className="text-xs text-slate-600 mt-1">Net Profit</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="w-8 h-8 text-blue-600" />
            </div>
            <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {activeAds}
            </div>
            <div className="text-xs text-slate-600 mt-1">Active Ads</div>
          </CardContent>
        </Card>
      </div>

      {/* Pie Charts Row */}
      <div className="grid [&>*]:min-w-0 md:grid-cols-3 gap-4 md:gap-6">
        <Card className="bg-white shadow-lg border-slate-200">
          <CardHeader>
            <CardTitle className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent text-sm">Sales Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {totalSales === 0 ? (
              <div className="h-48 flex items-center justify-center">
                <div className="text-center">
                  <PieIcon className="w-10 h-10 mx-auto mb-2 text-slate-600" />
                  <p className="text-slate-500 text-sm">No sales data</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer debounce={200} width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={salesBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {salesBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', color: '#F3F4F6' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200">
          <CardHeader>
            <CardTitle className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent text-sm">Order Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {(inTransit + delivered + returned) === 0 ? (
              <div className="h-48 flex items-center justify-center">
                <div className="text-center">
                  <Package className="w-10 h-10 mx-auto mb-2 text-slate-600" />
                  <p className="text-slate-500 text-sm">No order data</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer debounce={200} width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={orderDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {orderDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[(index + 1) % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', color: '#F3F4F6' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200">
          <CardHeader>
            <CardTitle className="bg-gradient-to-r from-blue-400 to-red-400 bg-clip-text text-transparent text-sm">Cost Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {(productCost + deliveryCharges + adsSpend + rtoCharges) === 0 ? (
              <div className="h-48 flex items-center justify-center">
                <div className="text-center">
                  <DollarSign className="w-10 h-10 mx-auto mb-2 text-slate-600" />
                  <p className="text-slate-500 text-sm">No cost data</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer debounce={200} width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={costBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {costBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', color: '#F3F4F6' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Comparison Chart */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-600">Revenue Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          {totalSales === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-500">No revenue data yet</p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer debounce={200} width="100%" height={300}>
              <BarChart data={revenueComparison}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
                <Legend />
                <Bar dataKey="revenue" fill="#10B981" />
                <Bar dataKey="expenses" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Order Analytics */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <CardTitle className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Order Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid [&>*]:min-w-0 grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <Truck className="w-6 h-6 text-yellow-400 mb-2" />
              <div className="text-2xl font-bold text-slate-800">{inTransit}</div>
              <div className="text-xs text-slate-500">In Transit</div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <Package className="w-6 h-6 text-green-600 mb-2" />
              <div className="text-2xl font-bold text-slate-800">{delivered}</div>
              <div className="text-xs text-slate-500">Delivered</div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <TrendingUp className="w-6 h-6 text-red-400 mb-2" />
              <div className="text-2xl font-bold text-slate-800">{returned}</div>
              <div className="text-xs text-slate-500">Returned</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gross Margin Analytics */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-600">Gross Margin Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid [&>*]:min-w-0 grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500/10 to-blue-500/10 rounded-lg border border-blue-500/20">
              <div className="text-xs text-slate-600 mb-1">Total Sales</div>
              <div className="text-xl font-bold text-blue-300">₹{totalSales}</div>
            </div>
            <div className="p-3 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
              <div className="text-xs text-slate-600 mb-1">Delivered Sales</div>
              <div className="text-xl font-bold text-green-300">₹{deliveredSales}</div>
            </div>
            <div className="p-3 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-lg border border-orange-500/20">
              <div className="text-xs text-slate-600 mb-1">Product Cost</div>
              <div className="text-xl font-bold text-orange-300">₹{productCost}</div>
            </div>
            <div className="p-3 bg-gradient-to-br from-blue-500/10 to-blue-500/10 rounded-lg border border-slate-200">
              <div className="text-xs text-slate-600 mb-1">Delivery Charges</div>
              <div className="text-xl font-bold text-purple-300">₹{deliveryCharges}</div>
            </div>
            <div className="p-3 bg-gradient-to-br from-blue-400/10 to-blue-700/10 rounded-lg border border-cyan-500/20">
              <div className="text-xs text-slate-600 mb-1">Gross Margin</div>
              <div className="text-xl font-bold text-cyan-300">₹{grossMargin}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Net Margin Analytics */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <CardTitle className="bg-gradient-to-r from-blue-400 to-rose-400 bg-clip-text text-transparent">Net Margin Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid [&>*]:min-w-0 grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-red-500/10 to-rose-500/10 rounded-lg border border-red-500/20">
              <div className="text-xs text-slate-600 mb-2">Ads Spend</div>
              <div className="text-2xl font-bold text-red-300">₹{adsSpend}</div>
            </div>
            <div className="p-4 bg-gradient-to-br from-orange-500/10 to-blue-500/10 rounded-lg border border-orange-500/20">
              <div className="text-xs text-slate-600 mb-2">RTO Charges</div>
              <div className="text-2xl font-bold text-orange-300">₹{rtoCharges}</div>
            </div>
            <div className="p-4 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
              <div className="text-xs text-slate-600 mb-2">Net Profit</div>
              <div className="text-2xl font-bold text-green-300">₹{netProfit}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
